/**
 * Slot Check Engagement Excel — branded Summary + Detail.
 * Dependency-free OOXML writer (community xlsx cannot emit cell styles).
 */
import { downloadBlob } from './scePdf';

export type SceExcelKpis = {
  uniqueActives: number;
  accountsCreated: number;
  accountsLinked: number;
  sessions: number;
  avgLogins: number;
};

export type SceExcelDetailRow = {
  month: string;
  ageBand: string;
  gender: string;
  activeUniqueAccounts: number;
  activeLogins: number;
  accountsCreated: number;
  accountsLinked: number;
  isPartial?: boolean;
};

export type SceExcelInput = {
  scopeLabel?: string;
  fromMonth: string;
  toMonth: string;
  ageLabel: string;
  genderLabel: string;
  generatedAt: Date;
  kpis: SceExcelKpis;
  priorKpis?: SceExcelKpis | null;
  priorFromMonth?: string;
  priorToMonth?: string;
  detailRows: SceExcelDetailRow[];
};

export function sanitizeSceToken(token: string): string {
  return (
    (token || 'SlotCheck')
      .replace(/[^\w\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 48) || 'SlotCheck'
  );
}

export function buildSceExcelFilename(from: string, to: string): string {
  return `SCE_${from}_${to}.xlsx`;
}

export function buildSceExcelSummaryFilename(from: string, to: string): string {
  return `SCE_${from}_${to}_Summary.xlsx`;
}

export function shiftMonth(ym: string, delta: number): string {
  const [y0, m0] = ym.split('-').map(Number);
  const idx = y0 * 12 + (m0 - 1) + delta;
  const y = Math.floor(idx / 12);
  const m = (idx % 12) + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

export function monthSpanCount(fromMonth: string, toMonth: string): number {
  const [fy, fm] = fromMonth.split('-').map(Number);
  const [ty, tm] = toMonth.split('-').map(Number);
  return Math.max(1, (ty - fy) * 12 + (tm - fm) + 1);
}

export function priorEqualLengthWindow(
  fromMonth: string,
  toMonth: string
): { priorFrom: string; priorTo: string; span: number } {
  const span = monthSpanCount(fromMonth, toMonth);
  return {
    priorFrom: shiftMonth(fromMonth, -span),
    priorTo: shiftMonth(fromMonth, -1),
    span,
  };
}

/** Alias for callers that import priorWindow. */
export const priorWindow = priorEqualLengthWindow;
export const monthSpan = monthSpanCount;

const xmlEscape = (s: string) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

type Cell =
  | { t: 's'; v: string; s?: number }
  | { t: 'n'; v: number; s?: number; z?: string };

type Sheet = {
  name: string;
  rows: Cell[][];
  merges: string[];
  colWidths: number[];
  freezeRow?: number;
  autoFilter?: string;
  rowHeights?: Record<number, number>;
};

function crc32(buf: Uint8Array): number {
  let c = ~0;
  for (let i = 0; i < buf.length; i++) {
    c ^= buf[i];
    for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ 0xedb88320 : c >>> 1;
  }
  return ~c >>> 0;
}
function u16(n: number) {
  return new Uint8Array([n & 255, (n >>> 8) & 255]);
}
function u32(n: number) {
  return new Uint8Array([n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255]);
}
function concat(parts: Uint8Array[]) {
  const len = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  parts.forEach((p) => {
    out.set(p, o);
    o += p.length;
  });
  return out;
}
function zipStore(files: Array<{ name: string; data: Uint8Array }>): Blob {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  const enc = new TextEncoder();
  files.forEach((f) => {
    const name = enc.encode(f.name);
    const crc = crc32(f.data);
    const local = concat([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(f.data.length),
      u32(f.data.length),
      u16(name.length),
      u16(0),
      name,
      f.data,
    ]);
    locals.push(local);
    centrals.push(
      concat([
        u32(0x02014b50),
        u16(20),
        u16(20),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(crc),
        u32(f.data.length),
        u32(f.data.length),
        u16(name.length),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(0),
        u32(offset),
        name,
      ])
    );
    offset += local.length;
  });
  const central = concat(centrals);
  const end = concat([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(offset),
    u16(0),
  ]);
  return new Blob([concat([...locals, central, end])], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function colLetter(i: number): string {
  let n = i;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

function momPctLabel(current: number, prior: number | null | undefined): { text: string; up: boolean | null } {
  if (prior == null || !Number.isFinite(prior)) return { text: 'n/a MoM', up: null };
  if (prior === 0) {
    if (current === 0) return { text: '0% MoM', up: null };
    return { text: '▲ new MoM', up: true };
  }
  const pct = ((current - prior) / prior) * 100;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return { text: '0% MoM', up: null };
  if (rounded > 0) return { text: `▲ ${rounded}% MoM`, up: true };
  return { text: `▼ ${Math.abs(rounded)}% MoM`, up: false };
}

function buildStylesXml(): string {
  // style indexes used below
  // 0 default
  // 1 headerDark fill + white bold 18
  // 2 headerDark fill + white bold 11 (meta)
  // 3 headerDark fill + gold bold 11 SlotCheck
  // 4 section title bold 12
  // 5 scope card fill + muted label 9
  // 6 scope card fill + bold value 11
  // 7 kpi card fill + gold top (via border) label 9
  // 8 kpi card large bold 18
  // 9 kpi mom up green
  // 10 kpi mom down red
  // 11 kpi mom flat muted
  // 12 how-to box fill
  // 13 detail header dark white bold
  // 14 detail body
  // 15 number int
  // 16 number avg
  // 17 subtitle muted
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2">
    <numFmt numFmtId="164" formatCode="#,##0"/>
    <numFmt numFmtId="165" formatCode="#,##0.00"/>
  </numFmts>
  <fonts count="12">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/></font>
    <font><b/><sz val="18"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FFF6BD16"/><name val="Calibri"/></font>
    <font><b/><sz val="12"/><color rgb="FF1A2332"/><name val="Calibri"/></font>
    <font><sz val="9"/><color rgb="FF6B7280"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><color rgb="FF111827"/><name val="Calibri"/></font>
    <font><b/><sz val="18"/><color rgb="FF111827"/><name val="Calibri"/></font>
    <font><b/><sz val="10"/><color rgb="FF059669"/><name val="Calibri"/></font>
    <font><b/><sz val="10"/><color rgb="FFDC2626"/><name val="Calibri"/></font>
    <font><sz val="10"/><color rgb="FF6B7280"/><name val="Calibri"/></font>
    <font><sz val="10"/><color rgb="FF374151"/><name val="Calibri"/></font>
  </fonts>
  <fills count="7">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF121820"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFFBEB"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1E2840"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/></patternFill></fill>
  </fills>
  <borders count="4">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFE5E7EB"/></left>
      <right style="thin"><color rgb="FFE5E7EB"/></right>
      <top style="medium"><color rgb="FFF6BD16"/></top>
      <bottom style="thin"><color rgb="FFE5E7EB"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FFE5E7EB"/></left>
      <right style="thin"><color rgb="FFE5E7EB"/></right>
      <top style="thin"><color rgb="FFE5E7EB"/></top>
      <bottom style="thin"><color rgb="FFE5E7EB"/></bottom>
    </border>
    <border>
      <left style="thin"><color rgb="FF45526B"/></left>
      <right style="thin"><color rgb="FF45526B"/></right>
      <top style="thin"><color rgb="FF45526B"/></top>
      <bottom style="thin"><color rgb="FF45526B"/></bottom>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="18">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="3" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="0" fontId="5" fillId="3" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="6" fillId="3" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="5" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="164" fontId="7" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyNumberFormat="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="8" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="9" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="10" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="11" fillId="3" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment wrapText="1" vertical="top"/></xf>
    <xf numFmtId="0" fontId="2" fillId="5" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="2" xfId="0" applyBorder="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyBorder="1"/>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyBorder="1"/>
    <xf numFmtId="165" fontId="7" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyNumberFormat="1"><alignment horizontal="left" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`;
}

function sheetToXml(sheet: Sheet, shared: string[]): string {
  const sharedIndex = (s: string) => {
    let i = shared.indexOf(s);
    if (i < 0) {
      shared.push(s);
      i = shared.length - 1;
    }
    return i;
  };

  let maxC = 0;
  sheet.rows.forEach((r) => {
    maxC = Math.max(maxC, r.length);
  });
  const dim = `A1:${colLetter(Math.max(0, maxC - 1))}${Math.max(1, sheet.rows.length)}`;

  const colsXml = sheet.colWidths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('');

  const rowsXml = sheet.rows
    .map((row, rIdx) => {
      const r = rIdx + 1;
      const ht = sheet.rowHeights?.[r];
      const htAttr = ht ? ` ht="${ht}" customHeight="1"` : '';
      const cells = row
        .map((cell, cIdx) => {
          if (!cell) return '';
          const ref = `${colLetter(cIdx)}${r}`;
          const sAttr = cell.s != null ? ` s="${cell.s}"` : '';
          if (cell.t === 'n') {
            return `<c r="${ref}"${sAttr}><v>${cell.v}</v></c>`;
          }
          const si = sharedIndex(cell.v);
          return `<c r="${ref}" t="s"${sAttr}><v>${si}</v></c>`;
        })
        .join('');
      return `<row r="${r}"${htAttr}>${cells}</row>`;
    })
    .join('');

  const merges =
    sheet.merges.length > 0
      ? `<mergeCells count="${sheet.merges.length}">${sheet.merges
          .map((m) => `<mergeCell ref="${m}"/>`)
          .join('')}</mergeCells>`
      : '';

  const freeze = sheet.freezeRow
    ? `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${sheet.freezeRow}" topLeftCell="A${
        sheet.freezeRow + 1
      }" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
    : `<sheetViews><sheetView workbookViewId="0"/></sheetViews>`;

  const autoFilter = sheet.autoFilter ? `<autoFilter ref="${sheet.autoFilter}"/>` : '';

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  ${freeze}
  <dimension ref="${dim}"/>
  <sheetFormatPr defaultRowHeight="16"/>
  <cols>${colsXml}</cols>
  <sheetData>${rowsXml}</sheetData>
  ${autoFilter}
  ${merges}
</worksheet>`;
}

function empty(n: number): Cell[] {
  return Array.from({ length: n }, () => ({ t: 's', v: '' }));
}

function buildSummary(input: SceExcelInput): Sheet {
  const cols = 12;
  const rows: Cell[][] = [];
  const merges: string[] = [];
  const rowHeights: Record<number, number> = {};

  const push = (cells: Cell[], h?: number) => {
    const r = [...cells];
    while (r.length < cols) r.push({ t: 's', v: '' });
    rows.push(r);
    if (h) rowHeights[rows.length] = h;
  };

  // Header rows 1-3
  push(
    [
      { t: 's', v: 'SlotCheck', s: 3 },
      { t: 's', v: '', s: 1 },
      { t: 's', v: '', s: 1 },
      { t: 's', v: '', s: 1 },
      { t: 's', v: '', s: 1 },
      { t: 's', v: '', s: 1 },
      { t: 's', v: (input.scopeLabel || 'Slot Check'), s: 2 },
      { t: 's', v: '', s: 2 },
      { t: 's', v: '', s: 2 },
      { t: 's', v: '', s: 2 },
      { t: 's', v: '', s: 2 },
      { t: 's', v: '', s: 2 },
    ],
    20
  );
  merges.push('A1:F1', 'G1:L1');

  push(
    [
      { t: 's', v: 'Slot Check Engagement — Summary', s: 1 },
      ...empty(5).map((c) => ({ ...c, s: 1 })),
      {
        t: 's',
        v: `${input.fromMonth} → ${input.toMonth} UTC`,
        s: 2,
      },
      ...empty(5).map((c) => ({ ...c, s: 2 })),
    ],
    28
  );
  merges.push('A2:F2', 'G2:L2');

  const gen = `Generated ${input.generatedAt.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  push(
    [
      { t: 's', v: '', s: 1 },
      ...empty(5).map((c) => ({ ...c, s: 1 })),
      { t: 's', v: gen, s: 2 },
      ...empty(5).map((c) => ({ ...c, s: 2 })),
    ],
    18
  );
  merges.push('A3:F3', 'G3:L3');

  push(empty(cols), 8);
  push([{ t: 's', v: 'Report scope', s: 4 }, ...empty(cols - 1)]);

  // Scope cards — label row + value row, 3 cols each
  const scope: Array<[string, string]> = [
    ['SCOPE', 'Slot Check (global)'],
    ['MONTHS (UTC)', `${input.fromMonth} – ${input.toMonth}`],
    ['AGE BANDS', input.ageLabel],
    ['GENDER', input.genderLabel],
  ];
  const labelRow: Cell[] = [];
  const valueRow: Cell[] = [];
  scope.forEach(([lab, val], i) => {
    const base = i * 3;
    while (labelRow.length < base) {
      labelRow.push({ t: 's', v: '' });
      valueRow.push({ t: 's', v: '' });
    }
    labelRow.push({ t: 's', v: lab, s: 5 }, { t: 's', v: '', s: 5 }, { t: 's', v: '', s: 5 });
    valueRow.push({ t: 's', v: val, s: 6 }, { t: 's', v: '', s: 6 }, { t: 's', v: '', s: 6 });
    const L = colLetter(base);
    const R = colLetter(base + 2);
    const lr = rows.length + 1;
    const vr = rows.length + 2;
    merges.push(`${L}${lr}:${R}${lr}`, `${L}${vr}:${R}${vr}`);
  });
  push(labelRow, 16);
  push(valueRow, 20);
  push(empty(cols), 8);

  const priorNote =
    input.priorFromMonth && input.priorToMonth
      ? `Totals for filtered range · MoM vs prior equal-length window (${input.priorFromMonth} – ${input.priorToMonth})`
      : 'Totals for filtered range · MoM vs prior equal-length window';

  push([{ t: 's', v: 'At a glance', s: 4 }, ...empty(cols - 1)]);
  push([{ t: 's', v: priorNote, s: 5 }, ...empty(cols - 1).map((c) => ({ ...c, s: 5 }))]);
  merges.push(`A${rows.length}:L${rows.length}`);

  const kpis: Array<{ label: string; value: number; avg?: boolean; cur: number; prior: number }> = [
    {
      label: 'Active Unique Accounts',
      value: input.kpis.uniqueActives,
      cur: input.kpis.uniqueActives,
      prior: input.priorKpis?.uniqueActives ?? NaN,
    },
    {
      label: 'Accounts Linked',
      value: input.kpis.accountsLinked,
      cur: input.kpis.accountsLinked,
      prior: input.priorKpis?.accountsLinked ?? NaN,
    },
    {
      label: 'New Accounts',
      value: input.kpis.accountsCreated,
      cur: input.kpis.accountsCreated,
      prior: input.priorKpis?.accountsCreated ?? NaN,
    },
    {
      label: 'Active Logins',
      value: input.kpis.sessions,
      cur: input.kpis.sessions,
      prior: input.priorKpis?.sessions ?? NaN,
    },
    {
      label: 'Avg logins / active',
      value: input.kpis.avgLogins,
      avg: true,
      cur: input.kpis.avgLogins,
      prior: input.priorKpis?.avgLogins ?? NaN,
    },
  ];

  const kpiLabelRow: Cell[] = [];
  const kpiValueRow: Cell[] = [];
  const kpiMomRow: Cell[] = [];
  kpis.forEach((k, i) => {
    const base = i * 3;
    while (kpiLabelRow.length < base) {
      kpiLabelRow.push({ t: 's', v: '' });
      kpiValueRow.push({ t: 's', v: '' });
      kpiMomRow.push({ t: 's', v: '' });
    }
    const mom = momPctLabel(k.cur, Number.isFinite(k.prior) ? k.prior : null);
    const momStyle = mom.up === true ? 9 : mom.up === false ? 10 : 11;
    kpiLabelRow.push({ t: 's', v: k.label, s: 7 }, { t: 's', v: '', s: 7 }, { t: 's', v: '', s: 7 });
    kpiValueRow.push(
      { t: 'n', v: k.value, s: k.avg ? 17 : 8 },
      { t: 's', v: '', s: k.avg ? 17 : 8 },
      { t: 's', v: '', s: k.avg ? 17 : 8 }
    );
    kpiMomRow.push({ t: 's', v: mom.text, s: momStyle }, { t: 's', v: '', s: momStyle }, { t: 's', v: '', s: momStyle });
    const L = colLetter(base);
    const R = colLetter(base + 2);
    const a = rows.length + 1;
    const b = rows.length + 2;
    const c = rows.length + 3;
    merges.push(`${L}${a}:${R}${a}`, `${L}${b}:${R}${b}`, `${L}${c}:${R}${c}`);
  });
  push(kpiLabelRow, 16);
  push(kpiValueRow, 26);
  push(kpiMomRow, 18);
  push(empty(cols), 10);

  push([{ t: 's', v: 'How to use this workbook', s: 4 }, ...empty(cols - 1)]);
  const how =
    '• Summary — partner snapshot of filtered totals and MoM vs the prior equal-length window.\n' +
    '• Detail — Month × Age band × Gender grain (CSV parity) under the same filters.\n' +
    '• Charts sheet deferred in v1 — use Export PDF / Chart PNGs for chart packs.\n' +
    'Source: Slot Check Engagement · Cognito group slotcheck-enterprise-reporting.';
  push([{ t: 's', v: how, s: 12 }, ...empty(cols - 1).map((c) => ({ ...c, s: 12 }))], 64);
  merges.push(`A${rows.length}:L${rows.length}`);

  return {
    name: 'Summary',
    rows,
    merges,
    colWidths: Array.from({ length: cols }, () => 12),
    rowHeights,
  };
}

function buildDetail(input: SceExcelInput): Sheet {
  const headers = [
    'Month',
    'Age Band',
    'Gender',
    'Active Unique Accounts',
    'Sessions',
    'Avg logins / active',
    'New Accounts',
    'Accounts Linked',
    'Partial',
  ];
  const rows: Cell[][] = [
    headers.map((h) => ({ t: 's', v: h, s: 13 })),
  ];
  input.detailRows.forEach((r) => {
    const uniques = r.activeUniqueAccounts || 0;
    const sessions = r.activeLogins || 0;
    const avg = uniques === 0 ? 0 : sessions / uniques;
    rows.push([
      { t: 's', v: r.month, s: 14 },
      { t: 's', v: r.ageBand, s: 14 },
      { t: 's', v: r.gender, s: 14 },
      { t: 'n', v: uniques, s: 15 },
      { t: 'n', v: sessions, s: 15 },
      { t: 'n', v: avg, s: 16 },
      { t: 'n', v: r.accountsCreated || 0, s: 15 },
      { t: 'n', v: r.accountsLinked || 0, s: 15 },
      { t: 's', v: r.isPartial ? 'Yes' : '', s: 14 },
    ]);
  });
  const last = Math.max(1, rows.length);
  return {
    name: 'Detail',
    rows,
    merges: [],
    colWidths: [12, 12, 12, 14, 12, 12, 16, 14, 10],
    freezeRow: 1,
    autoFilter: `A1:I${last}`,
  };
}

function packWorkbook(sheets: Sheet[]): Blob {
  const shared: string[] = [];
  const sheetXmls = sheets.map((s) => sheetToXml(s, shared));

  const sharedXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${shared.length}" uniqueCount="${shared.length}">
${shared.map((s) => `<si><t>${xmlEscape(s)}</t></si>`).join('')}
</sst>`;

  const sheetEntries = sheets
    .map((s, i) => `    <sheet name="${xmlEscape(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('\n');

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
${sheetEntries}
  </sheets>
</workbook>`;

  const sheetRels = sheets
    .map(
      (_, i) =>
        `  <Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
    )
    .join('\n');
  const stylesRid = sheets.length + 1;
  const sharedRid = sheets.length + 2;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheetRels}
  <Relationship Id="rId${stylesRid}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId${sharedRid}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
</Relationships>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const sheetOverrides = sheets
    .map(
      (_, i) =>
        `  <Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join('\n');

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${sheetOverrides}
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
</Types>`;

  const enc = new TextEncoder();
  const files = [
    { name: '[Content_Types].xml', data: enc.encode(contentTypes) },
    { name: '_rels/.rels', data: enc.encode(rootRels) },
    { name: 'xl/workbook.xml', data: enc.encode(workbookXml) },
    { name: 'xl/_rels/workbook.xml.rels', data: enc.encode(workbookRels) },
    { name: 'xl/styles.xml', data: enc.encode(buildStylesXml()) },
    { name: 'xl/sharedStrings.xml', data: enc.encode(sharedXml) },
    ...sheetXmls.map((xml, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: enc.encode(xml),
    })),
  ];
  return zipStore(files);
}

export async function buildSlotCheckEngagementExcel(input: SceExcelInput): Promise<Blob> {
  return packWorkbook([buildSummary(input), buildDetail(input)]);
}

/** Summary sheet only (no Detail). */
export async function buildSlotCheckEngagementExcelSummary(input: SceExcelInput): Promise<Blob> {
  return packWorkbook([buildSummary(input)]);
}

export async function downloadSlotCheckEngagementExcel(input: SceExcelInput): Promise<void> {
  const blob = await buildSlotCheckEngagementExcel(input);
  downloadBlob(blob, buildSceExcelFilename(input.fromMonth, input.toMonth));
}

export async function downloadSlotCheckEngagementExcelSummary(input: SceExcelInput): Promise<void> {
  const blob = await buildSlotCheckEngagementExcelSummary(input);
  downloadBlob(blob, buildSceExcelSummaryFilename(input.fromMonth, input.toMonth));
}
